import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  titleClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
}

/**
 * A generic modal component with customizable header and content
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  titleClassName = "text-xl font-semibold",
  contentClassName = "p-6",
  headerClassName = "px-6 py-4 bg-purple-50 flex justify-between items-center",
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the modal
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl max-w-xl w-full overflow-hidden animate-fade-in"
      >
        <div className={headerClassName}>
          <h3 className={titleClassName}>{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className={contentClassName}>{children}</div>
      </div>
    </div>
  );
}
